/** Shared MiraOps controls. Semantic colors come from tokens and never follow the accent. */
import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { ChevronDown, X } from './glyphs.tsx';
import { SOURCE_LABELS, rovingIndex, typeaheadIndex, type DocState, type EvidenceKind, type OrderStatus, type Urgency } from '@miraops/contracts';
import { useI18n } from '../lib/i18n.tsx';

export function Button({ variant = 'secondary', size = 'md', icon, className = '', children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'quiet' | 'danger' | 'link'; size?: 'sm' | 'md' | 'lg'; icon?: ReactNode;
}) {
  return <button type="button" className={`btn btn-${variant} btn-${size} ${className}`} {...rest}>{icon}{children}</button>;
}

export function StatusBadge({ status }: { status: OrderStatus }) {
  const { t } = useI18n();
  return <span className={`badge status-${status}`}>{t(`status.${status}`)}</span>;
}

/** Provider source chip. Gray in tables; the legend explains LCD in the footer. */
export function SourceBadge({ source }: { source: string | null }) {
  if (!source) return <span className="muted">—</span>;
  const label = SOURCE_LABELS[source];
  return <span className="source" title={label?.name ?? source}>{label?.short ?? source}</span>;
}

const docTone: Record<DocState, string> = {
  ready: 'success', missing: 'danger', not_due: 'neutral', unchecked: 'attention', restricted: 'neutral', stale: 'attention', unavailable: 'neutral',
};
export function DocChip({ kind, state, bare = false }: { kind: EvidenceKind; state: DocState; bare?: boolean }) {
  const { t } = useI18n();
  return <span className={`doc-chip tone-${docTone[state]}`} title={`${t(`kind.${kind}`)}: ${t(`doc.${state}`)}`}>{bare ? t(`doc.${state}`) : `${t(`kind.${kind}`)}: ${t(`doc.${state}`)}`}</span>;
}

export function UrgencyBadge({ urgency }: { urgency: Urgency }) {
  const { t } = useI18n();
  if (!urgency) return null;
  return <span className={`badge lfd-badge tone-${urgency === 'soon' ? 'attention' : urgency === 'today' ? 'attention' : 'danger'}`}>{t(`urgency.${urgency}`)}</span>;
}

export function Banner({ tone, children, action }: { tone: 'info' | 'attention' | 'danger' | 'success'; children: ReactNode; action?: ReactNode }) {
  return (
    <div className={`banner tone-${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <span>{children}</span>
      {action}
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return <span className="spinner" role="status" aria-label={label} />;
}

export function EmptyState({ title, detail, action }: { title: string; detail?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {detail && <p className="muted">{detail}</p>}
      {action}
    </div>
  );
}

/** Moves focus to the button at `index` inside `container` (roving focus). */
function focusAt(container: HTMLElement | null, selector: string, index: number) {
  (container?.querySelectorAll<HTMLElement>(selector)[index])?.focus();
}

/**
 * Radio group: one tab stop (the checked option); arrow keys, Home and End move and select,
 * as in a native radio group.
 */
export function Segmented<T extends string>({ value, options, onChange, label, className = '' }: { value: T; options: { value: T; label: ReactNode }[]; onChange: (v: T) => void; label: string; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const current = Math.max(0, options.findIndex(o => o.value === value));
  const onKey = (e: ReactKeyboardEvent) => {
    const next = rovingIndex(e.key, current, options.map(() => false));
    if (next === null) return;
    e.preventDefault(); onChange(options[next]!.value); focusAt(ref.current, '[role=radio]', next);
  };
  return (
    <div ref={ref} className={`segmented ${className}`} role="radiogroup" aria-label={label} onKeyDown={onKey}>
      {options.map((o, i) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} tabIndex={i === current ? 0 : -1} className={o.value === value ? 'on' : ''} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/**
 * Underlined page tabs. One tab stop; Left/Right/Home/End move focus and activate (tabs here
 * switch instantly). Pass `panelId` to link the tabs to the panel they control.
 */
export function Tabs<T extends string>({ value, tabs, onChange, label, panelId }: { value: T; tabs: { value: T; label: ReactNode; disabled?: boolean; title?: string }[]; onChange: (v: T) => void; label: string; panelId?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  const current = Math.max(0, tabs.findIndex(tab => tab.value === value));
  const onKey = (e: ReactKeyboardEvent) => {
    const next = rovingIndex(e.key, current, tabs.map(tab => !!tab.disabled), 'horizontal');
    if (next === null) return;
    e.preventDefault(); onChange(tabs[next]!.value); focusAt(ref.current, '[role=tab]', next);
  };
  return (
    <div ref={ref} className="tabs" role="tablist" aria-label={label} onKeyDown={onKey}>
      {tabs.map((tab, i) => (
        <button key={tab.value} id={`${id}-${tab.value}`} type="button" role="tab" aria-selected={tab.value === value} aria-controls={panelId} disabled={tab.disabled} title={tab.title}
          tabIndex={i === current ? 0 : -1} className={`tab${tab.value === value ? ' on' : ''}`} onClick={() => onChange(tab.value)}>{tab.label}</button>
      ))}
    </div>
  );
}

/** Closes a popover on outside click or Escape. */
export function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) close(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, close]);
  return ref;
}

/**
 * A select-styled trigger with a list of options (single choice). Opening moves focus to the
 * selected option; arrows, Home, End and typing a letter move; Enter or Space chooses; Escape
 * and Tab close and return focus to the trigger.
 */
export function Dropdown<T extends string>({ value, options, onChange, label, icon, className = '', align = 'start', renderValue }: {
  value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string; icon?: ReactNode; className?: string; align?: 'start' | 'end';
  renderValue?: (v: T) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: '', at: 0 });
  const close = (refocus: boolean) => { setOpen(false); if (refocus) trigger.current?.focus(); };
  const ref = useDismiss(open, () => close(true));
  const current = options.findIndex(o => o.value === value);
  const [active, setActive] = useState(0);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const start = Math.max(0, current);
    setActive(start);
    focusAt(list.current, '[role=option]', start);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const move = (i: number) => { setActive(i); focusAt(list.current, '[role=option]', i); };
  const onListKey = (e: ReactKeyboardEvent) => {
    const next = rovingIndex(e.key, active, options.map(() => false), 'vertical');
    if (next !== null) { e.preventDefault(); move(next); return; }
    if (e.key === 'Tab') { close(false); return; }
    // Escape closes only this list, not a dialog it sits in.
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); return; }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && e.key !== ' ') {
      const now = Date.now();
      typed.current = { text: (now - typed.current.at < 600 ? typed.current.text : '') + e.key, at: now };
      const hit = typeaheadIndex(options.map(o => o.label), typed.current.text, active);
      if (hit !== null) { e.preventDefault(); move(hit); }
    }
  };
  const onTriggerKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); }
  };
  return (
    <div className={`dropdown ${className}`} ref={ref}>
      <button ref={trigger} type="button" className="select" aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? id : undefined} aria-label={label}
        onClick={() => setOpen(o => !o)} onKeyDown={onTriggerKey}>
        {icon}<span className="select-value">{renderValue ? renderValue(value) : current >= 0 ? options[current]!.label : label}</span><ChevronDown size={16} className="select-chevron" aria-hidden />
      </button>
      {open && (
        <ul ref={list} className={`menu menu-${align}`} role="listbox" id={id} aria-label={label} onKeyDown={onListKey}>
          {options.map((o, i) => (
            <li key={o.value} role="presentation">
              <button type="button" role="option" aria-selected={o.value === value} tabIndex={i === active ? 0 : -1} className={o.value === value ? 'on' : ''}
                onClick={() => { onChange(o.value); close(true); }}>{o.label}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Colored initials circle. Color comes from a stable hash of the label. */
const AVATAR_TONES = ['blue', 'green', 'teal', 'purple', 'orange', 'rose', 'slate'];
export function Avatar({ text, seed, size = 'md', tone }: { text: string; seed?: string; size?: 'sm' | 'md' | 'lg'; tone?: string }) {
  let h = 0;
  for (const ch of seed ?? text) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return <span className={`avatar avatar-${size} avatar-${tone ?? AVATAR_TONES[h % AVATAR_TONES.length]}`} aria-hidden>{text}</span>;
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
/**
 * Keeps Tab and Shift+Tab cycling inside a modal (a native modal still lets focus leave to the
 * browser's own controls after the last element).
 */
export function wrapFocus(e: ReactKeyboardEvent, container: HTMLElement | null) {
  if (e.key !== 'Tab' || !container) return;
  const items = [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(el => el.offsetParent !== null || el === document.activeElement);
  if (!items.length) return;
  const first = items[0]!, last = items[items.length - 1]!;
  if (e.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}

/**
 * Side drawer as a native modal dialog: the browser keeps Tab inside it and makes the page
 * behind it inert. Focus returns to whatever opened it. Escape and the overlay close it.
 * The open/close lifecycle runs once per drawer, so a new onClose each render never re-runs it.
 */
export function Drawer({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const { t } = useI18n();
  const dialog = useRef<HTMLDialogElement>(null);
  const panel = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const d = dialog.current;
    const opener = document.activeElement as HTMLElement | null;
    if (d && !d.open) { try { d.showModal(); } catch { d.setAttribute('open', ''); } }
    panel.current?.focus();
    const onCancel = (e: Event) => { e.preventDefault(); closeRef.current(); };
    d?.addEventListener('cancel', onCancel);
    return () => {
      d?.removeEventListener('cancel', onCancel);
      if (d?.open) d.close();
      if (opener && opener.isConnected) opener.focus();
    };
  }, []);
  return (
    <dialog className="drawer-layer" ref={dialog} aria-label={title} onKeyDown={e => wrapFocus(e, panel.current)}>
      <div className="drawer-overlay" onClick={() => closeRef.current()} />
      <aside className="drawer" tabIndex={-1} ref={panel} aria-label={title}>
        <header className="drawer-head">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" aria-label={t('detail.close')} onClick={() => closeRef.current()}><X size={22} /></button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer && <footer className="drawer-foot">{footer}</footer>}
      </aside>
    </dialog>
  );
}

export function Checkbox({ checked, onChange, label, indeterminate = false, hideLabel = true }: { checked: boolean; onChange: (v: boolean) => void; label: string; indeterminate?: boolean; hideLabel?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate; }, [indeterminate]);
  return (
    <label className="checkbox" onClick={e => e.stopPropagation()}>
      <input ref={ref} type="checkbox" checked={checked} aria-label={hideLabel ? label : undefined} onChange={e => onChange(e.target.checked)} />
      {!hideLabel && <span>{label}</span>}
    </label>
  );
}
